import { useState } from "react";
import { ArrowRight, Building2, KeyRound, Mail, ShieldCheck, Sparkles, Zap } from "lucide-react";

import { ErrorApi } from "../api/cliente.ts";
import type { CuentaDisponible } from "../api/tipos.ts";
import { usarSesion } from "../sesion/ContextoSesion.tsx";
import { MarcaCompleta } from "../componentes/Marca.tsx";
import { PanelPublico, type Argumento } from "../componentes/PanelPublico.tsx";
import { Aviso, Boton, Campo, cx } from "../componentes/ui.tsx";
import { usarTema } from "../ganchos/tema.ts";

const ARGUMENTOS: Argumento[] = [
  {
    icono: Zap,
    titulo: "Te avisa en cuanto te pagan",
    texto: "La alerta del banco entra por correo y en segundos sabes quién te pagó y por qué factura. Nadie copia y pega nada.",
  },
  {
    icono: ShieldCheck,
    titulo: "Nunca dice que cuadra si no cuadra",
    texto:
      "Cuando la evidencia no alcanza, pregunta. Un falso positivo cuesta más que mil preguntas.",
  },
  {
    icono: Sparkles,
    titulo: "Aprende de lo que confirmas",
    texto: "Decir de quién es un pago una vez basta para que el siguiente se resuelva solo.",
  },
];

export function Entrar() {
  const { entrar } = usarSesion();
  const { tema } = usarTema();
  const [correo, setCorreo] = useState("");
  const [contrasena, setContrasena] = useState("");
  const [cuentas, setCuentas] = useState<CuentaDisponible[] | null>(null);
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);

  async function intentar(slug?: string) {
    setEnviando(true);
    setError("");
    try {
      const r = await entrar(correo.trim(), contrasena, slug);
      // 300: el usuario pertenece a varias cuentas y hay que elegir una.
      if (r.elige) setCuentas(r.elige);
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : "No se pudo conectar con 4drando.");
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="grid min-h-dvh lg:grid-cols-2">
      <PanelPublico
        titular="Facturé, me pagaron, cuadré."
        entrada="4drando lee las alertas del banco que tu empresa ya recibe y te dice quién pagó, por qué factura y qué falta por cobrar."
        argumentos={ARGUMENTOS}
        pie="Tu sesión viaja en una cookie que este navegador no deja leer a ningún script."
      />

      {/* — formulario — */}
      <main className="flex items-center justify-center px-5 py-12 sm:px-10">
        <div className="w-full max-w-sm space-y-8 subir">
          <div className="space-y-2 lg:hidden">
            <MarcaCompleta />
          </div>

          {cuentas ? (
            <ElegirCuenta
              cuentas={cuentas}
              enviando={enviando}
              alElegir={(slug) => void intentar(slug)}
              alVolver={() => {
                setCuentas(null);
                setError("");
              }}
            />
          ) : (
            <>
              <div className="space-y-1.5">
                <h2 className="text-2xl font-semibold tracking-tight">Entra a tu panel</h2>
                <p className="text-sm text-tenue">
                  Usa el correo con el que te invitaron a la cuenta.
                </p>
              </div>

              <form
                className="space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  void intentar();
                }}
              >
                <Campo
                  etiqueta="Correo"
                  type="email"
                  icono={Mail}
                  autoComplete="username"
                  placeholder="tu@empresa.co"
                  required
                  value={correo}
                  onChange={(e) => setCorreo(e.target.value)}
                />
                <Campo
                  etiqueta="Contraseña"
                  type="password"
                  icono={KeyRound}
                  autoComplete="current-password"
                  placeholder="••••••••••••"
                  required
                  value={contrasena}
                  onChange={(e) => setContrasena(e.target.value)}
                />

                {error && <Aviso titulo="No pudimos entrar">{error}</Aviso>}

                <Boton
                  type="submit"
                  variante="primario"
                  tamano="lg"
                  bloque
                  cargando={enviando}
                  icono={ArrowRight}
                >
                  Entrar
                </Boton>
              </form>

              <p className="text-center text-xs leading-relaxed text-tenue">
                ¿Olvidaste la contraseña? Pídele a quien administra la cuenta que te envíe un enlace
                de restablecimiento desde <span className="font-medium text-tinta">Equipo</span>.
              </p>
            </>
          )}
        </div>
      </main>

      {/* El tema se aplica igual aquí: quien entra de noche no merece un fogonazo. */}
      <span className="sr-only">Tema {tema}</span>
    </div>
  );
}

function ElegirCuenta({
  cuentas,
  enviando,
  alElegir,
  alVolver,
}: {
  cuentas: CuentaDisponible[];
  enviando: boolean;
  alElegir: (slug: string) => void;
  alVolver: () => void;
}) {
  return (
    <div className="space-y-6">
      <div className="space-y-1.5">
        <h2 className="text-2xl font-semibold tracking-tight">¿A cuál cuenta entras?</h2>
        <p className="text-sm text-tenue">
          Tu usuario tiene acceso a varias empresas. Cada una tiene sus propios pagos.
        </p>
      </div>

      <ul className="space-y-2">
        {cuentas.map((c) => (
          <li key={c.slug}>
            <button
              disabled={enviando}
              onClick={() => alElegir(c.slug)}
              className={cx(
                "group flex w-full items-center gap-3 rounded-xl border border-borde bg-superficie p-3.5 text-left",
                "transition-all hover:border-marca hover:bg-marca-suave disabled:opacity-60",
              )}
            >
              <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-superficie-2 text-tenue group-hover:bg-marca group-hover:text-white">
                <Building2 className="size-5" aria-hidden />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{c.nombre}</span>
                <span className="block truncate text-xs text-tenue">{c.slug}</span>
              </span>
              <ArrowRight className="size-4 shrink-0 text-tenue-2 group-hover:text-marca" aria-hidden />
            </button>
          </li>
        ))}
      </ul>

      <Boton variante="fantasma" bloque onClick={alVolver}>
        Usar otro correo
      </Boton>
    </div>
  );
}
